/**
 * The dead-deal comparison scope of the Pipeline grid.
 *
 * A saved view may read the live pipeline ('active', the default), the dead
 * deals kept for comparison ('dead'), or both ('all') — `FilterState.dealStatus`.
 * Everything the grid needs to say about the second and third lives here: the
 * badge on a dead row, the banner that says the rows are not live totals, the
 * merge of the two books, and the reason a view that reads dead deals cannot be
 * scheduled.
 */
import type { ReactNode } from 'react'
import { AlertTriangle, Ban, Loader2 } from 'lucide-react'
import type { Loan } from '@/types'
import { dealScopeOf, isDeadLoan } from './helpers'
import type { DealScope, FilterState } from './types'

/** What a dead deal reads as in a cell: the reason on hover, never a blank. */
function deadTitle(loan: Pick<Loan, 'deadReason' | 'killReasonCode' | 'killedAtStage'>): string {
  const reason = loan.deadReason || loan.killReasonCode || 'reason not recorded'
  return `Dead — ${reason}${loan.killedAtStage ? ` · died at ${loan.killedAtStage}` : ''}`
}

/** The red "Dead" mark on a killed deal. Renders nothing for a live one, so it
 *  is safe to place unconditionally in a cell. */
export function DeadBadge({
  loan,
}: {
  loan: Pick<Loan, 'dealStatus' | 'deadReason' | 'killReasonCode' | 'killedAtStage'>
}) {
  if (!isDeadLoan(loan)) return null
  return (
    <span
      title={deadTitle(loan)}
      className="inline-flex items-center gap-0.5 whitespace-nowrap rounded border border-red-400/25 bg-red-400/[0.08] px-1.5 py-0.5 text-[9px] font-medium text-red-300"
    >
      <Ban className="h-2.5 w-2.5" aria-hidden />
      Dead
    </span>
  )
}

/** A cell's content with the Dead mark beside it when the deal is dead; the
 *  content itself, untouched, when it is not. */
export function withDeadBadge(
  loan: Pick<Loan, 'dealStatus' | 'deadReason' | 'killReasonCode' | 'killedAtStage'>,
  content: ReactNode,
): ReactNode {
  if (!isDeadLoan(loan)) return content
  return (
    <span className="inline-flex items-center gap-1">
      {content}
      <DeadBadge loan={loan} />
    </span>
  )
}

/**
 * The two books, as one list for a scope.
 *
 * 'active' is the live book alone and 'dead' the dead book alone — whatever
 * else the other list holds. 'all' is both, each deal once: a deal named by
 * both lists (a kill landing between the two reads) is the dead record, the
 * newer fact.
 */
export function scopeLoans(scope: DealScope, active: Loan[], dead: Loan[]): Loan[] {
  if (scope === 'active') return active
  if (scope === 'dead') return dead
  const byId = new Map<string, Loan>()
  for (const loan of active) byId.set(loan.id, loan)
  for (const loan of dead) byId.set(loan.id, loan)
  return [...byId.values()]
}

/** True when the view reads dead deals, which no schedule may do: a daily
 *  change bar over them would report every one as "killed" noise, and a
 *  recurring delivery of a one-off comparison is not a report anyone asked for. */
export function readsDeadDeals(filters: Pick<FilterState, 'dealStatus'> | null | undefined): boolean {
  return dealScopeOf(filters) !== 'active'
}

/** The reason a schedule cannot be created on this view, or null when it can.
 *  The server enforces the same rule (422) — this is the words for the control. */
export function scheduleBlockedReason(
  filters: Pick<FilterState, 'dealStatus'> | null | undefined,
): string | null {
  return readsDeadDeals(filters)
    ? 'This view includes dead deals, so it cannot be scheduled. Schedule a view of the active pipeline instead.'
    : null
}

/** One-line reading of the scope for a banner. */
export function scopeBannerText(args: {
  scope: DealScope
  /** Dead deals on file (the book, before the grid's own filters). */
  deadCount: number
  /** Live deals on file. */
  activeCount: number
  /** Rows the grid shows after the view's filters. */
  shown: number
  /** The kill reason the view narrows its dead deals to, readable ("Parallel run reset"); then
   *  `deadCount` counts the dead deals with that reason. */
  reason?: string | null
}): string {
  const { scope, deadCount, activeCount, shown, reason } = args
  const noun = (n: number) => `${n} dead deal${n === 1 ? '' : 's'}${reason ? ` (${reason})` : ''}`
  if (scope === 'dead') {
    const narrowed = shown !== deadCount ? ` · ${shown} shown` : ''
    return `Comparison — ${noun(deadCount)}, excluded from live totals${narrowed}`
  }
  return (
    `Comparison — ${activeCount} active and ${noun(deadCount)} shown together. ` +
    `The stage rail and the figures above count the ${activeCount} active deals only.`
  )
}

/** The strip above the grid that says these rows are not the live pipeline. */
export function ScopeBanner({
  scope,
  deadCount,
  activeCount,
  shown,
  loading,
  error,
  needsLiveData,
  onRetry,
  reason,
}: {
  scope: DealScope
  deadCount: number
  activeCount: number
  shown: number
  reason?: string | null
  loading: boolean
  error: boolean
  /** A demo account has no dead deals to show — say so rather than show none. */
  needsLiveData: boolean
  onRetry: () => void
}) {
  if (scope === 'active') return null
  let body: ReactNode
  let tone = 'border-red-400/20 bg-red-500/[0.05] text-red-200'
  if (needsLiveData) {
    tone = 'border-amber-300/20 bg-amber-300/[0.05] text-amber-100'
    body = <>Dead-deal comparison needs live data — a demo account holds no dead deals.</>
  } else if (error) {
    body = (
      <>
        <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
        <span>The dead deals could not be loaded, so none are shown.</span>
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-red-300/30 px-1.5 py-0.5 text-[10px] text-red-100 hover:bg-red-400/10"
        >
          Retry
        </button>
      </>
    )
  } else if (loading) {
    body = (
      <>
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
        <span>Loading dead deals…</span>
      </>
    )
  } else {
    body = (
      <>
        <Ban className="h-3 w-3 shrink-0" aria-hidden />
        <span>{scopeBannerText({ scope, deadCount, activeCount, shown, reason })}</span>
      </>
    )
  }
  return (
    <div
      role="status"
      data-testid="dead-scope-banner"
      className={`flex flex-wrap items-center gap-1.5 border-b px-4 py-1 text-[11px] ${tone}`}
    >
      {body}
    </div>
  )
}
